<!-- webapp/frontend/src/App.vue -->
<template>
  <v-app>
    <Sidebar />
    <v-main>
      <v-container fluid class="pa-0 fill-height">
        <router-view :key="$store.getters['projects/currentProjectId']" />
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import Sidebar from '@/components/layout/Sidebar.vue'
import { mapActions, mapState } from 'vuex'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'

export default {
  name: 'App',
  components: {
    Sidebar
  },
  setup() {
    const auth = useAuthStore()
    return { auth }
  },
  computed: {
    ...mapState('levels', ['maturityLevels', 'riskLevels']),
    currentProjectId() {
      return this.$store.getters['projects/currentProjectId']
    }
  },
  watch: {
    'auth.isLoggedIn': {
      handler(newValue) {
        if (newValue) {
          this.initializeData()
        }
      }
    },
    currentProjectId(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        console.log(`Project changed from ${oldValue} to ${newValue}, refreshing levels...`)
        this.refreshLevels()
      }
    }
  },
  methods: {
    ...mapActions('levels', ['fetchMaturityLevels', 'fetchRiskLevels']),
    logStoreState() {
      const { isLoggedIn, user } = storeToRefs(this.auth)
      
      console.log('Store State:', {
        auth: {
          isLoggedIn: isLoggedIn.value,
          user: user.value,
          token: localStorage.getItem('token')
        },
        levels: {
          maturityLevels: this.maturityLevels,
          riskLevels: this.riskLevels
        }
      })
    },
    async refreshLevels() {
      if (!this.auth.isLoggedIn) return
      try {
        await Promise.all([
          this.fetchMaturityLevels({ apiService: api }),
          this.fetchRiskLevels({ apiService: api })
        ])
        this.logStoreState()
      } catch (error) {
        console.error('Failed to refresh levels:', error)
      }
    },
    async initializeData() {
      try {
        // Initialize projects first
        await this.$store.dispatch('projects/initializeProject')
        
        // Check if user has a last viewed project preference that overrides current selection
        // Only if no project is currently selected (to respect URL deep links)
        const user = this.auth.user
        const currentProjectId = this.$store.getters['projects/currentProjectId']
        
        if (!currentProjectId && user && user.last_project_id) {
            console.log(`Switching to user's last project: ${user.last_project_id}`)
            await this.$store.dispatch('projects/selectProject', user.last_project_id)
        }

        await this.refreshLevels()
      } catch (error) {
        console.error('Failed to initialize app data:', error)
      }
    }
  },
  async created() {
    console.log('App created - Initial state:', {
      isLoggedIn: this.auth.isLoggedIn,
      token: !!localStorage.getItem('token')
    })
    
    // Wait for auth to initialize
    await this.auth.initializeAuth()
    
    console.log('Auth initialized:', {
      isLoggedIn: this.auth.isLoggedIn,
      token: !!localStorage.getItem('token')
    })
    
    // Only fetch levels if authenticated
    if (this.auth.isLoggedIn) {
      await this.initializeData()
    } else {
      console.log('Not logged in - skipping level fetch')
    }
  }
}
</script>

<style>
* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: 'Roboto', sans-serif;
  background-color: #f5f5f5;
  color: #333;
}

.v-application {
  height: 100vh;
}

.v-main {
  height: 100%;
}

.v-container {
  height: 100%;
  max-height: 100%;
}
</style>